import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";

// Teacher interface (as you provided)
export interface Teacher {
    id: number;
    schoolId: number;
    employeeId: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    designation: string;
    department: string;
    qualification: string;
    dateOfBirth: string;
    joiningDate: string;
    gender: string;
    address: string;
    city: string;
    state: string;
    country: string;
    pinCode: string;
    emergencyContact: string;
    bloodGroup: string;
    aadharNumber: string;
    panNumber: string;
    bankName: string;
    accountNumber: string;
    ifscCode: string;
    salary: number;
    isActive: boolean;
    role: string;
    experience: string;
    specialization: string;
    status: boolean;
    enabled: boolean;
    classIds: number[];
    userId?: number;
    password?: string;
    teacherOfClass:string[];
}

export interface TeacherFormData extends Omit<Teacher, 'id' | 'designation' | 'bloodGroup' | 'salary'> {
    id?: number;
    password?: string;
    classIds: number[];
    teacherOfClass: string[];
}

export interface StaffClassMapping {
    id: number;
    classId: number;
    staffId?: number;
    teacher?: Teacher;
    teacherId?: number;
    sessionId?: number;
    className?: string;
    section?: string;
    [key: string]: any; // Allow for additional fields
}

const API_URL = `${process.env.REACT_APP_API_URL}/staff`;

// Fetch all teachers
const getAllTeachers = async (): Promise<Teacher[]> => {
  const response = await axios.get(API_URL);
  return response.data;
};

// Fetch staff by email (new API)
const getStaffByEmail = async (email: string): Promise<Teacher> => {
  const response = await axios.get(`${API_URL}/getStaffByEmail`, {
    params: { email },
  });
  return response.data;
};

// Fetch teacher by id
const getTeacherById = async (id: number): Promise<Teacher> => {
  const response = await axios.get(`${API_URL}/${id}`);
  return response.data;
};

// Fetch teachers by schoolId
const getTeachersBySchool = async (schoolId: number): Promise<Teacher[]> => {
  const response = await axios.get(`${API_URL}/school/${schoolId}`);
  return response.data;
};

// Fetch teacher by employeeId
const getTeacherByEmployeeId = async (employeeId: string): Promise<Teacher> => {
  const response = await axios.get(`${API_URL}/employee/${employeeId}`);
  return response.data;
};

// Fetch staff by staff and class
const getStaffByStaffAndClass = async (sessionId: number, classId: number): Promise<Teacher[]> => {
  const response = await axios.get(`${API_URL}/getByStaffAndClass`, {
    params: {
      sessionId, // Backend expects sessionId parameter
      classId, // Backend expects classId parameter
    },
  });
  return response.data;
};

// Fetch staff class mappings by sessionId
const getStaffClassMapping = async (sessionId: number): Promise<StaffClassMapping[]> => {
  const response = await axios.get(`${API_URL}/getStaffClassMapping`, {
    params: {
      sessionId,
    },
  });
  return response.data;
};

// Create a new teacher
const createTeacher = async (teacherData: TeacherFormData & { sessionId?: number }): Promise<Teacher> => {
  const { qualification, specialization, isActive, experience, classIds, ...payload } = teacherData;
  // Ensure teacherOfClass is included in payload and filter out empty strings
  if (teacherData.teacherOfClass) {
    payload.teacherOfClass = teacherData.teacherOfClass.filter((className: string) => className && className.trim() !== '');
  }
  if (teacherData.sessionId) payload.sessionId = teacherData.sessionId;
  // Set enabled to true when creating
  payload.enabled = true;
  const response = await axios.post(API_URL, payload);
  return response.data;
};

// Update an existing teacher
const updateTeacher = async (id: number, teacherData: TeacherFormData & { sessionId?: number }): Promise<Teacher> => {
  const { qualification, specialization, isActive, experience, classIds, ...payload } = teacherData;
  // Ensure teacherOfClass is included in payload and filter out empty strings
  if (teacherData.teacherOfClass) {
    payload.teacherOfClass = teacherData.teacherOfClass.filter((className: string) => className && className.trim() !== '');
  }
  if (!teacherData.password) {
    delete payload.password;
  }
  if (teacherData.sessionId) payload.sessionId = teacherData.sessionId;
  // Set enabled to true when updating
  payload.enabled = true;
  const response = await axios.put(`${API_URL}/${id}`, payload);
  return response.data;
};

// Delete a teacher
const deleteTeacher = async (id: number): Promise<void> => {
  await axios.delete(`${API_URL}/${id}`);
};

// Deactivate a teacher
const deactivateTeacher = async (id: number): Promise<Teacher> => {
  const response = await axios.put(`${API_URL}/${id}/deactivate`);
  return response.data;
};

export function useTeacher(schoolId?: number, tenantLoading: boolean = false) {
  const queryClient = useQueryClient();

  const teachersQuery = useQuery<Teacher[], Error>({
    queryKey: ["teachers", schoolId],
    queryFn: () => schoolId ? getTeachersBySchool(schoolId) : getAllTeachers(),
    enabled: !!schoolId && !tenantLoading,
  });

  // Staff by email (for TEACHER login)
  const useStaffByEmail = (email: string | undefined) =>
    useQuery<Teacher, Error>({
      queryKey: ["staff", "byEmail", email],
      queryFn: () => getStaffByEmail(email || ""),
      enabled: !!email && !tenantLoading,
      retry: (failureCount, error) => {
        // Don't retry on 404 errors (not found)
        if (error && "response" in error && (error as any).response?.status === 404) {
          return false;
        }
        return failureCount < 2;
      },
    });

  // Teacher by id
  const useTeacherById = (id: number) =>
    useQuery<Teacher, Error>({
      queryKey: ["teacher", id],
      queryFn: () => getTeacherById(id),
      enabled: !!id && !tenantLoading,
      retry: (failureCount, error) => {
        // Don't retry on 404 errors (teacher not found/deleted)
        if (error && 'response' in error && (error as any).response?.status === 404) {
          return false;
        }
        // Retry up to 3 times for other errors
        return failureCount < 3;
      },
    });

  // Teachers by schoolId
  const useTeachersBySchool = (schoolId: number) =>
    useQuery<Teacher[], Error>({
      queryKey: ["teachers", "school", schoolId],
      queryFn: () => getTeachersBySchool(schoolId),
      enabled: !!schoolId && !tenantLoading,
    });

  // Teacher by employeeId
  const useTeacherByEmployeeId = (employeeId: string) =>
    useQuery<Teacher, Error>({
      queryKey: ["teacher", "employee", employeeId],
      queryFn: () => getTeacherByEmployeeId(employeeId),
      enabled: !!employeeId && !tenantLoading,
    });

  // Staff by staff and class
  const useStaffByStaffAndClass = (sessionId: number | undefined, classId: number | undefined) =>
    useQuery<Teacher[], Error>({
      queryKey: ["staff", "staffAndClass", sessionId, classId],
      queryFn: () => getStaffByStaffAndClass(sessionId || 0, classId || 0),
      enabled: sessionId !== undefined && sessionId !== null && sessionId > 0 && classId !== undefined && classId !== null && classId > 0 && !tenantLoading,
    });

  // Staff class mappings by sessionId
  const useStaffClassMapping = (sessionId: number | undefined) =>
    useQuery<StaffClassMapping[], Error>({
      queryKey: ["staffClassMapping", sessionId],
      queryFn: () => getStaffClassMapping(sessionId || 0),
      enabled: sessionId !== undefined && sessionId !== null && sessionId > 0 && !tenantLoading,
    });

  // Create
  const createTeacherMutation = useMutation({
    mutationFn: createTeacher,
    onSuccess: () => {
      // Invalidate all teacher-related queries to ensure UI updates
      queryClient.invalidateQueries({ queryKey: ["teachers"] });
      // Also invalidate school-specific teacher queries
      if (schoolId) {
        queryClient.invalidateQueries({ queryKey: ["teachers", schoolId] });
        queryClient.invalidateQueries({ queryKey: ["teachers", "school", schoolId] });
      }
    },
  });

  // Update
  const updateTeacherMutation = useMutation({
    mutationFn: (args: { id: number; data: TeacherFormData & { sessionId?: number } }) =>
      updateTeacher(args.id, args.data),
    onSuccess: (data, variables) => {
      // Invalidate all teacher-related queries
      queryClient.invalidateQueries({ queryKey: ["teachers"] });
      // Invalidate specific teacher query to update individual teacher data
      queryClient.invalidateQueries({ queryKey: ["teacher", variables.id] });
      // Invalidate school-specific teacher queries
      if (schoolId) {
        queryClient.invalidateQueries({ queryKey: ["teachers", schoolId] });
        queryClient.invalidateQueries({ queryKey: ["teachers", "school", schoolId] });
      }
    },
  });

  // Delete
  const deleteTeacherMutation = useMutation({
    mutationFn: deleteTeacher,
    onSuccess: (_, deletedTeacherId) => {
      // Invalidate all teacher-related queries to ensure UI updates
      queryClient.invalidateQueries({ queryKey: ["teachers"] });
      // Invalidate the specific teacher query to clear cached data
      queryClient.invalidateQueries({ queryKey: ["teacher", deletedTeacherId] });
      // Also invalidate school-specific teacher queries
      if (schoolId) {
        queryClient.invalidateQueries({ queryKey: ["teachers", schoolId] });
        queryClient.invalidateQueries({ queryKey: ["teachers", "school", schoolId] });
      }
    },
  });

  // Deactivate
  const deactivateTeacherMutation = useMutation({
    mutationFn: deactivateTeacher,
    onSuccess: (_, deactivatedTeacherId) => {
      queryClient.invalidateQueries({ queryKey: ["teachers"] });
      // Invalidate the specific teacher query to clear cached data
      queryClient.invalidateQueries({ queryKey: ["teacher", deactivatedTeacherId] });
      // Also invalidate school-specific teacher queries
      if (schoolId) {
        queryClient.invalidateQueries({ queryKey: ["teachers", schoolId] });
        queryClient.invalidateQueries({ queryKey: ["teachers", "school", schoolId] });
      }
    },
  });

  return {
    teachersQuery,
    useStaffByEmail,
    useTeacherById,
    useTeachersBySchool,
    useTeacherByEmployeeId,
    useStaffByStaffAndClass,
    useStaffClassMapping,
    createTeacherMutation,
    updateTeacherMutation,
    deleteTeacherMutation,
    deactivateTeacherMutation,
  };
}
